import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShieldAlert, ArrowLeft, CheckCircle2, Star } from "lucide-react";
import api, { inr } from "@/lib/api";
import { RxBadge } from "@/components/ProductCard";
import { useCart } from "@/context/CartContext";
import { Reveal, EASE } from "@/components/motion";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [qty, setQtyLocal] = useState(1);
  const [variantLabel, setVariantLabel] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    setProduct(null);
    setQtyLocal(1);
    setVariantLabel(null);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  }, [id]);

  const variants = product?.variants || [];
  const activeVariant = variants.find((v) => v.label === variantLabel) || variants[0] || null;
  const price = activeVariant ? activeVariant.price : product?.price;
  const packLabel = activeVariant ? activeVariant.label : product?.pack;

  if (notFound) return (
    <div className="pt-40 pb-32 text-center" data-testid="product-not-found">
      <p className="font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Product not found</p>
      <Link to="/shop" className="text-[#0F5A47] font-mono-ed text-xs uppercase tracking-[0.2em] mt-4 inline-block">← Back to catalog</Link>
    </div>
  );

  if (!product) return <p className="pt-40 pb-32 text-center font-mono-ed text-xs uppercase tracking-[0.25em] text-slate-400">Loading…</p>;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-28 pb-10" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 font-mono-ed text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-[#0F5A47] transition-colors" data-testid="product-back-link">
        <ArrowLeft size={13} /> Catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-14 mt-10">
        <motion.div
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, ease: EASE }}
          className="relative spotlight"
        >
          <div className="relative z-10 border hairline bg-white p-3">
            <img src={(product.image || "").replace("_SL3840_QL80", "_SL800_QL70")} alt={product.name} className="w-full aspect-[4/5] object-cover" data-testid="product-detail-image" />
          </div>
          <div className="absolute top-6 left-6 z-20"><RxBadge rx={product.rx} /></div>
        </motion.div>

        <Reveal>
          <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-[#0F5A47]">{product.category}</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl mt-3 text-balance" data-testid="product-detail-name">{product.name}</h1>
          <p className="font-serif-ed italic text-xl text-slate-500 mt-4">{packLabel}</p>

          <div className="flex items-center gap-1.5 text-xs text-slate-600 mt-3" data-testid="product-detail-rating">
            <div className="flex gap-0.5">
              {[0, 1, 2, 3, 4].map((s) => (
                <Star key={s} size={13} className={s < Math.round(product.rating ?? 4.7) ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
              ))}
            </div>
            <span className="font-semibold">{product.rating ?? 4.7}</span>
            <span className="text-slate-400">| {(product.reviews ?? 120).toLocaleString("en-IN")} verified reviews</span>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-6 max-w-sm" data-testid="product-detail-pricing">
            <div className="border border-slate-200 rounded-lg bg-slate-50 px-4 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">MRP <span className="normal-case font-medium">/ strip</span></p>
              <p className="font-mono-ed text-lg font-semibold text-slate-600" data-testid="product-detail-mrp">{inr(product.mrp)}</p>
            </div>
            <div className="border border-emerald-200 rounded-lg bg-emerald-50 px-4 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600">PTR <span className="normal-case font-medium">/ strip</span></p>
              <p className="font-mono-ed text-xl font-bold text-emerald-900" data-testid="product-detail-price">{inr(product.price)}</p>
            </div>
          </div>

          {variants.length > 0 && (
            <div className="mt-6" data-testid="variant-selector">
              <p className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2.5">Pack Size</p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => (
                  <button
                    key={v.label}
                    data-testid={`variant-chip-${v.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setVariantLabel(v.label)}
                    className={`px-4 py-2.5 border font-mono-ed text-xs rounded-lg transition-colors duration-300 active:scale-95 ${(variantLabel || variants[0]?.label) === v.label ? "bg-[#0F5A47] text-white border-[#0F5A47]" : "bg-white hairline hover:border-[#0F5A47]"}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
              {activeVariant && (
                <div className="mt-4 border border-emerald-200 bg-emerald-50/60 rounded-xl p-4 max-w-md" data-testid="pack-rate-panel">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold text-sm text-slate-900">{activeVariant.label}</span>
                    <span className="font-mono-ed font-bold text-lg text-emerald-900" data-testid="pack-price">{inr(activeVariant.price)}</span>
                  </div>
                  <p className="font-mono-ed text-[11px] text-slate-500 mt-1.5 leading-relaxed" data-testid="pack-calc-explainer">
                    {inr(product.price)} (strip PTR) × 10 strips = {inr(product.price * 10)} per box × {parseInt(activeVariant.label, 10)} boxes = <span className="font-semibold text-[#0B0F17]">{inr(activeVariant.price)}</span>
                  </p>
                  {activeVariant.mrp && <p className="font-mono-ed text-[10px] text-slate-400 mt-1">Pack MRP: {inr(activeVariant.mrp)}</p>}
                  {(() => { const bx = parseInt(activeVariant.label, 10) * qty; const fb = bx >= 10 ? Math.floor(bx / 2) : 0; return fb > 0 ? <p className="font-mono-ed text-[11px] font-semibold text-emerald-700 mt-2" data-testid="scheme-line">Free-box scheme applied: +{fb} boxes FREE ({(fb * 10).toLocaleString("en-IN")} strips · ₹0.00)</p> : null; })()}
                  <p className="font-mono-ed text-[10px] text-slate-400 mt-2">Free-box scheme: 10→5 · 20→10 · 30→15 · 50→25 · 100→50 boxes free</p>
                </div>
              )}
            </div>
          )}

          <div className="border hairline mt-8 divide-y divide-[#E2E8F0]">
            <div className="grid grid-cols-3 px-5 py-4">
              <span className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400">Composition</span>
              <span className="col-span-2 text-sm" data-testid="product-composition">{product.composition}</span>
            </div>
            <div className="grid grid-cols-3 px-5 py-4">
              <span className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400">Batch</span>
              <span className="col-span-2 text-sm font-mono-ed">{product.batch}</span>
            </div>
            <div className="grid grid-cols-3 px-5 py-4">
              <span className="font-mono-ed text-[10px] uppercase tracking-[0.2em] text-slate-400">Stock</span>
              <span className="col-span-2 text-sm flex items-center gap-2" data-testid="product-stock">
                {product.stock > 0 ? <><CheckCircle2 size={14} className="text-[#0F5A47]" /> {product.stock} units available</> : "Out of stock"}
              </span>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed mt-6 whitespace-pre-line" data-testid="product-description">{product.description}</p>

          {product.rx && (
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 px-4 py-3.5 mt-6" data-testid="product-rx-notice">
              <ShieldAlert size={16} className="text-red-700 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-900 leading-relaxed">
                This is a prescription (Rx) medicine supplied strictly to licensed pharmacies, clinics & hospitals. No prescription upload needed — your Drug License & GST number are collected right after payment.
              </p>
            </div>
          )}

          <div className="flex items-center gap-4 mt-8">
            <div className="flex items-center border hairline bg-white">
              <button data-testid="product-qty-minus" onClick={() => setQtyLocal(Math.max(1, qty - 1))} className="px-4 py-3.5 hover:bg-slate-100 transition-colors"><Minus size={14} /></button>
              <span className="px-4 font-mono-ed" data-testid="product-qty-value">{qty}</span>
              <button data-testid="product-qty-plus" onClick={() => setQtyLocal(qty + 1)} className="px-4 py-3.5 hover:bg-slate-100 transition-colors"><Plus size={14} /></button>
            </div>
            <button
              data-testid="product-detail-add-to-cart"
              onClick={() => add(product, qty, activeVariant?.label || null)}
              disabled={product.stock <= 0}
              className="flex-1 bg-[#0F5A47] text-white py-4 font-mono-ed text-xs uppercase tracking-[0.25em] hover:bg-[#0A4234] transition-colors duration-300 active:scale-[0.98] disabled:opacity-40"
            >
              Add to Cart — {inr(price * qty)}
            </button>
          </div>
          {activeVariant && (
            <p className="font-mono-ed text-[11px] text-slate-500 mt-3" data-testid="box-count-note">
              You are ordering: {qty} × {activeVariant.label} = <span className="font-semibold text-[#0B0F17]">{qty * parseInt(activeVariant.label, 10)} boxes</span> ({(qty * parseInt(activeVariant.label, 10) * 10).toLocaleString("en-IN")} strips total)
              {(() => { const fb = Math.floor((qty * parseInt(activeVariant.label, 10)) / 2); return (qty * parseInt(activeVariant.label, 10)) >= 10 ? <span className="text-emerald-700 font-semibold"> + {fb} boxes FREE</span> : null; })()}
            </p>
          )}
        </Reveal>
      </div>
    </div>
  );
}
